import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Auth } from '@freelancer/auth';
import {
  Datastore,
  DatastoreCollection,
  DatastoreDocument,
} from '@freelancer/datastore';
import {
  ArticlesCollection,
  BidsCollection,
  CanViewEmployerProfileCollection,
  CertificationsCollection,
  Currency,
  EducationsCollection,
  EmployerReputation,
  EmployerReputationsRehireRatesCollection,
  ExperiencesCollection,
  FreelancerReputationsRehireRatesCollection,
  FreelancerReputationWithRehire,
  LocalHourlyRate,
  MembershipBenefitsCollection,
  OnlineOfflineCollection,
  Pool,
  PortfolioItem,
  PortfoliosCollection,
  ProfileViewUser,
  ProfileViewUsersCollection,
  ProjectsCollection,
  ProjectViewProject,
  ProjectViewProjectsCollection,
  PublicationsCollection,
  ReviewsCollection,
  TopSkillsCollection,
  User,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { Seo } from '@freelancer/seo';
import { Tracking } from '@freelancer/tracking';
import { ModalService } from '@freelancer/ui';
import { Assets } from '@freelancer/ui/assets';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardBorderRadius, CardSize } from '@freelancer/ui/card';
import { ContainerSize } from '@freelancer/ui/container';
import { Margin } from '@freelancer/ui/margin';
import { ModalColor, ModalSize } from '@freelancer/ui/modal';
import { BackgroundColor, LayoutType } from '@freelancer/ui/page-layout';
import {
  FontColor,
  FontType,
  FontWeight,
  TextTransform,
} from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { assertNever, isDefined, toNumber } from '@freelancer/utils';
import { FrontendBidStatusApi, RoleApi } from 'api-typings/common/common';
import {
  BidAwardStatusApi,
  ProjectCollaborationPermissionApi,
  ProjectStatusApi,
  ProjectTypeApi,
  ReviewTypeApi,
} from 'api-typings/projects/projects';
import {
  ArticleStateApi,
  CorporateStatusApi,
  OnlineOfflineStatusApi,
} from 'api-typings/users/users';
import {
  FollowUserToastItem,
  RecommendUserToastItem,
} from 'app/hire-me/hire-me.component';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import { Md5 } from 'ts-md5';
import { LoginOrSignup } from '../login-signup/login-signup-modal/login-signup-modal.model';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from './user-profile-award-project.types';
import { UserProfileViewState } from './user-profile-editable-ui/user-profile-editable-ui.model';
import { SplitButtonClickEvent } from './user-profile-hire-me/user-profile-hire-me.component';

interface CoverImageStyle {
  readonly backgroundImage?: string;
}

interface OpenModalParams {
  readonly event: SplitButtonClickEvent;
  readonly params: {
    readonly refProject?: ProjectViewProject;
  };
}

export enum UserProfileSection {
  SUMMARY = 'profile-summary',
  PORTFOLIO = 'profile-portfolio',
  REVIEWS = 'profile-reviews',
  RESUME = 'profile-resume',
  EXAMS = 'profile-exams',
  HIRE_ME = 'profile-hire-me',
}

@Component({
  selector: 'app-user-profile',
  template: `
    <app-navigation-secondary-profile
      *ngIf="showSecondaryNavigation$ | flAsync"
      [flHideMobile]="true"
    ></app-navigation-secondary-profile>

    <app-navigation-secondary-skills
      *ngIf="showSecondarySkillsNavigation$ | flAsync"
      flTrackingSection="UserProfile"
      [flHideMobile]="true"
      [userId$]="profileUserId$"
    ></app-navigation-secondary-skills>

    <fl-bit #pageTop></fl-bit>

    <app-user-profile-header
      *ngIf="showStickyHeader$ | flAsync"
      flTrackingSection="UserProfile"
      [awardProjectBid$]="awardProjectBid$"
      [awardProjectHireMeState$]="awardProjectHireMeState$"
      [hireMeUser$]="hireMeUser$"
      [isAgentLoggedUser$]="isAgentLoggedInUser$"
      [isOnline$]="isOnline$"
      [profileUserRole$]="profileUserRole$"
      [profileViewUserDoc]="profileViewUsersDocument"
      [resumeExists$]="resumeExists$"
      [showUsersPortfolio$]="showUsersPortfolio$"
      [showUsersResume$]="showUsersResume$"
      [showUsersReviews$]="showUsersReviews$"
      [userProfileSection]="userProfileSection"
      [userReputation$]="userReputation$"
      [flHideMobile]="true"
      (hireMeClicked)="
        handleSplitButtonClickEvent(SplitButtonClickEvent.HIRE_ME)
      "
    ></app-user-profile-header>

    <fl-bit class="Page" flTrackingSection="UserProfile">
      <fl-bit
        class="CoverPhoto"
        [ngStyle]="coverImageToShow$ | flAsync"
      ></fl-bit>

      <fl-bit class="PageContent">
        <fl-toast-alert
          i18n="Success followed user message"
          [id]="FollowUserToastItem.FOLLOW_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
        >
          You're now following
          {{ (hireMeUser$ | flAsync)?.profileUserDisplayName }}.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error following user message"
          [id]="FollowUserToastItem.FOLLOW_ERROR"
          [type]="ToastAlertType.ERROR"
        >
          Error following user.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error target user disallowed following user message"
          [id]="FollowUserToastItem.FOLLOW_ERROR_USER_DOES_NOT_ALLOW_FOLLOWING"
          [timeout]="5000"
          [type]="ToastAlertType.INFO"
        >
          This user does not allow users to follow them.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error already following user message"
          [id]="FollowUserToastItem.FOLLOW_ERROR_ALREADY_FOLLOWING"
          [timeout]="5000"
          [type]="ToastAlertType.INFO"
        >
          You are already following this user.
        </fl-toast-alert>

        <fl-toast-alert
          [id]="FollowUserToastItem.FOLLOW_ERROR_OVER_LIMIT"
          [timeout]="5000"
          [type]="ToastAlertType.INFO"
        >
          <ng-container
            *flFeature="Feature.MEMBERSHIPS; else followLimitNoUpsell"
            i18n="Error overlimit following user message"
          >
            Your membership plan only allows
            {{ maxFollowLimit$ | flAsync }} follows. Upgrade
            <fl-link
              flTrackingLabel="UserFollowMaxLimitUpgrade"
              link="/membership"
            >
              here.
            </fl-link>
          </ng-container>
          <ng-template #followLimitNoUpsell>
            <ng-container
              i18n="
                Error overlimit following user message with no membership upsell
              "
            >
              You've reached your maximum number of follows.
            </ng-container>
          </ng-template>
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Success unfollowing user message"
          [id]="FollowUserToastItem.UNFOLLOW_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
        >
          Successfully unfollowed user.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error unfollowing user message"
          [id]="FollowUserToastItem.UNFOLLOW_ERROR"
          [type]="ToastAlertType.ERROR"
        >
          Error unfollowing user.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Success recommending user message"
          [id]="RecommendUserToastItem.RECOMMEND_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
        >
          You have successfully recommended
          {{ (hireMeUser$ | flAsync)?.profileUserDisplayName }}
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error recommending user message"
          [id]="RecommendUserToastItem.RECOMMEND_ERROR"
          [type]="ToastAlertType.ERROR"
        >
          Error recommending user.
        </fl-toast-alert>
        <fl-container [size]="ContainerSize.DESKTOP_LARGE">
          <app-user-profile-award-project-banner
            *ngIf="showAwardProjectBanner$ | flAsync"
            [flHideMobile]="true"
            [awardProjectBid$]="awardProjectBid$"
            [flMarginBottom]="Margin.MID"
            [isDeloitteUserProfile$]="isDeloitteUserProfile$"
          ></app-user-profile-award-project-banner>

          <fl-grid>
            <!-- TODO: T156109 - add ngIf here based on if contents exist -->
            <fl-col
              [ngClass]="{
                HeaderButtons: true,
                EditMode: showEditCoverPhoto$ | flAsync
              }"
              [colDesktopSmall]="
                (isAgentUserProfile$ | flAsync) === true ? 12 : 9
              "
              [flMarginBottom]="Margin.SMALL"
            >
              <app-user-profile-edit-cover-picture
                *ngIf="showEditCoverPhoto$ | flAsync"
                (imageCropped)="handleImageCropped($event)"
                (imageReset)="handleImageReset()"
              ></app-user-profile-edit-cover-picture>

              <ng-container *ngIf="isLoggedIn$ | flAsync">
                <app-user-profile-switch-views
                  *ngIf="showSwitchUsers$ | flAsync"
                  [currentType]="viewProfileAs$ | flAsync"
                  (typeChange)="handleProfileViewTypeChange($event)"
                ></app-user-profile-switch-views>
              </ng-container>
            </fl-col>

            <fl-col
              [colDesktopSmall]="
                (isAgentUserProfile$ | flAsync) === true ? 12 : 9
              "
            >
              <fl-anchor-scroll
                [name]="UserProfileSection.SUMMARY"
                [offset]="90"
              ></fl-anchor-scroll>
              <app-user-profile-summary
                [defaultCurrency]="defaultCurrency"
                [featuredReviewsCollection]="featuredReviewsCollection"
                [flMarginBottom]="Margin.MID"
                [isAgent$]="isAgentUserProfile$"
                [isCorporate$]="isCorporate$"
                [isDeloitteUserProfile$]="isDeloitteUserProfile$"
                [isLoggedIn$]="isLoggedIn$"
                [isOnline$]="isOnline$"
                [isViewerProfileOwner$]="isViewerProfileOwner$"
                [localHourlyRate$]="localHourlyRate$"
                [triggerCancelChanges$]="triggerCancelSummaryChanges$"
                [triggerSaveChanges$]="triggerSaveSummaryChanges$"
                [localHourlyRateLoading$]="localHourlyRateLoading$"
                [profileUserId$]="profileUserId$"
                [userDocument]="profileViewUsersDocument"
                [userReputation$]="userReputation$"
                [viewProfileAs$]="viewProfileAs$"
                [viewState$]="currentViewState$"
                (featuredReviewClick)="
                  handleToggleFeaturedReviewEditModeChange(true)
                "
                (toggleViewMode)="handleToggleViewMode($event)"
                (savingSummary)="handleSavingSummary($event)"
                #summary
              ></app-user-profile-summary>

              <app-user-profile-breadcrumbs
                *ngIf="!(isLoggedIn$ | flAsync)"
                [flMarginBottom]="Margin.MID"
                [profileDisplayName$]="profileDisplayName$"
                [profileUserId$]="profileUserId$"
                [profileViewUsersDocument]="profileViewUsersDocument"
              ></app-user-profile-breadcrumbs>

              <fl-bit [flHideDesktop]="true">
                <fl-anchor-scroll
                  [name]="UserProfileSection.HIRE_ME"
                  [offset]="60"
                ></fl-anchor-scroll>
                <ng-container
                  *ngTemplateOutlet="showHireMeAction"
                ></ng-container>
              </fl-bit>

              <app-user-profile-banner
                *ngIf="isViewerProfileOwner$ | flAsync"
                [profileViewUsersDocument]="profileViewUsersDocument"
                [flMarginBottom]="Margin.MID"
              ></app-user-profile-banner>

              <fl-anchor-scroll
                [name]="UserProfileSection.PORTFOLIO"
                [offset]="90"
              ></fl-anchor-scroll>
              <fl-bit #portfolio>
                <ng-container *ngIf="!isShowcase; else showcaseTemplate">
                  <app-user-profile-portfolio
                    *ngIf="showUsersPortfolio$ | flAsync"
                    [awardProjectBid$]="awardProjectBid$"
                    [awardProjectHireMeState$]="awardProjectHireMeState$"
                    [flMarginBottom]="Margin.MID"
                    [hireMeUser$]="hireMeUser$"
                    [isCorporate$]="isCorporate$"
                    [isLoggedIn$]="isLoggedIn$"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                    [localHourlyRate$]="localHourlyRate$"
                    [profileUserId$]="profileUserId$"
                    [profileViewUsersDocument]="profileViewUsersDocument"
                    [skillsCollection]="skillsCollection"
                    [userReputation$]="userReputation$"
                  ></app-user-profile-portfolio>
                </ng-container>
                <ng-template #showcaseTemplate>
                  <app-user-profile-showcase
                    *ngIf="showUsersPortfolio$ | flAsync"
                    [flMarginBottom]="Margin.MID"
                    [isLoggedIn$]="isLoggedIn$"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                    [profileUserId$]="profileUserId$"
                    [user$]="profileViewUser$"
                  ></app-user-profile-showcase>
                </ng-template>
              </fl-bit>

              <fl-anchor-scroll
                [name]="UserProfileSection.REVIEWS"
                [offset]="90"
              ></fl-anchor-scroll>
              <fl-bit #reviews>
                <app-user-profile-reviews
                  *ngIf="showUsersReviews$ | flAsync"
                  [flMarginBottom]="Margin.MID"
                  [isCorporateProfile]="isCorporate$ | flAsync"
                  [isFeaturedReviewEditMode]="
                    isFeaturedReviewEditMode$ | flAsync
                  "
                  [isLoggedIn$]="isLoggedIn$"
                  [isProfileOwner]="isViewerProfileOwner$ | flAsync"
                  [userDocument]="profileViewUsersDocument"
                  [userReputation$]="userReputation$"
                  [usersId$]="profileUserId$"
                  [viewProfileAs$]="viewProfileAs$"
                  (setFeaturedReviewEditMode)="
                    handleToggleFeaturedReviewEditModeChange($event)
                  "
                ></app-user-profile-reviews>
              </fl-bit>

              <app-user-profile-practitioner-information
                *ngIf="isDeloitteUserProfile$ | flAsync"
                [user$]="user$"
                [isProfilePage]="true"
                [isViewerProfileOwner$]="isViewerProfileOwner$"
              ></app-user-profile-practitioner-information>

              <app-user-profile-expert-communities
                *ngIf="hasCommunity$ | flAsync"
                [user$]="user$"
                [isViewerProfileOwner$]="isViewerProfileOwner$"
              ></app-user-profile-expert-communities>

              <fl-anchor-scroll
                [name]="UserProfileSection.RESUME"
                [offset]="90"
              ></fl-anchor-scroll>
              <fl-bit #resume>
                <fl-bit *ngIf="showUsersResume$ | flAsync">
                  <app-user-profile-experiences
                    [experiencesCollection]="experiencesCollection"
                    [flMarginBottom]="Margin.MID"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                  ></app-user-profile-experiences>

                  <app-user-profile-educations
                    [educationsCollection]="educationsCollection"
                    [flMarginBottom]="Margin.MID"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                  ></app-user-profile-educations>

                  <app-user-profile-certifications
                    [certificationsCollection]="certificationsCollection"
                    [flMarginBottom]="Margin.MID"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                  ></app-user-profile-certifications>

                  <app-user-profile-publications
                    [flMarginBottom]="Margin.MID"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                    [publicationsCollection]="publicationsCollection"
                  ></app-user-profile-publications>
                </fl-bit>
              </fl-bit>
            </fl-col>

            <fl-col
              *ngIf="!(isAgentUserProfile$ | flAsync)"
              [colDesktopSmall]="3"
            >
              <fl-bit [flShowDesktop]="true">
                <ng-container
                  *ngTemplateOutlet="showHireMeAction"
                ></ng-container>
              </fl-bit>

              <app-user-profile-verifications
                *flFeature="Feature.VERIFICATIONS"
                [flMarginBottom]="Margin.MID"
                [flHideMobile]="true"
                [isLoggedIn]="isLoggedIn$ | flAsync"
                [isOwner]="isViewerProfileOwner$ | flAsync"
                [profileViewUsersDocument]="profileViewUsersDocument"
              ></app-user-profile-verifications>

              <ng-container *flFeature="Feature.EXAMS">
                <fl-anchor-scroll
                  [name]="UserProfileSection.EXAMS"
                  [offset]="90"
                ></fl-anchor-scroll>
                <app-user-profile-exams
                  [flMarginBottom]="Margin.MID"
                  [isLoggedIn$]="isLoggedIn$"
                  [isViewerProfileOwner$]="isViewerProfileOwner$"
                  [profileViewUsersDocument]="profileViewUsersDocument"
                ></app-user-profile-exams>
              </ng-container>
              <ng-container *flFeature="Feature.PROFILE_SKILLS">
                <app-user-profile-skills
                  [flMarginBottom]="Margin.MID"
                  [isLoggedIn$]="isLoggedIn$"
                  [isViewerProfileOwner$]="isViewerProfileOwner$"
                  [skillsCollection]="skillsCollection"
                  [userId$]="profileUserId$"
                  (triggerSkillsUpdate)="handleTriggerSkillsUpdate()"
                ></app-user-profile-skills>
              </ng-container>

              <app-user-profile-browse-freelancers
                *flFeature="Feature.DEPRECATED_SEARCH_PAGES"
                [flMarginBottom]="Margin.MID"
                [userProfileId$]="profileUserId$"
              ></app-user-profile-browse-freelancers>

              <app-user-profile-browse-showcases
                *flFeature="Feature.SHOWCASE"
                [userProfileId$]="profileUserId$"
                [flMarginBottom]="Margin.MID"
              ></app-user-profile-browse-showcases>

              <app-user-profile-articles
                *flFeature="Feature.COMMUNITY"
                [articlesCollection]="articlesCollection"
                [flMarginBottom]="Margin.MID"
              ></app-user-profile-articles>

              <app-user-profile-report
                *ngIf="showReportUser$ | flAsync"
                [flMarginBottom]="Margin.SMALL"
                [profileViewUserDocument]="profileViewUsersDocument"
              ></app-user-profile-report>

              <app-user-profile-next-previous-users
                *ngIf="showNextPreviousUsers$ | flAsync"
                [flMarginBottom]="Margin.SMALL"
                [profileUserId$]="profileUserId$"
              ></app-user-profile-next-previous-users>
            </fl-col>
          </fl-grid>
        </fl-container>
      </fl-bit>
      <ng-template #showHireMeAction>
        <app-user-profile-hire-me-action
          *ngIf="!(isViewerProfileOwner$ | flAsync)"
          [awardProjectBid$]="awardProjectBid$"
          [awardProjectHireMeState$]="awardProjectHireMeState$"
          [flMarginBottom]="Margin.MID"
          [hireMeUser$]="hireMeUser$"
          [isDeloitteUserProfile$]="isDeloitteUserProfile$"
          [isLoggedIn$]="isLoggedIn$"
          [profileDisplayName$]="profileDisplayName$"
          (splitButtonClick)="handleSplitButtonClickEvent($event)"
        ></app-user-profile-hire-me-action>
      </ng-template>
    </fl-bit>

    <app-user-profile-footer
      flTrackingSection="UserProfile"
      [awardProjectBid$]="awardProjectBid$"
      [awardProjectHireMeState$]="awardProjectHireMeState$"
      [flShowMobile]="true"
      [hireMeUser$]="hireMeUser$"
      [isLoggedIn$]="isLoggedIn$"
      [isViewerOwner$]="isViewerProfileOwner$"
      [savingSummaryChanges$]="savingSummaryChanges$"
      [userDocument]="profileViewUsersDocument"
      [viewState$]="currentViewState$"
      (editProfile)="handleToggleViewMode(UserProfileViewState.EDIT)"
      (cancelEditProfile)="handleCancelEditProfile()"
      (saveEditProfile)="handleSaveEditProfile()"
      (optionClicked)="handleSplitButtonClickEvent($event)"
      (showToastAlert)="showToastAlert($event)"
    ></app-user-profile-footer>

    <fl-toast-alert
      i18n="Invite freelancer to project success toast alert"
      [id]="'invite-freelancer-success-toast-alert'"
      [type]="ToastAlertType.SUCCESS"
    >
      Invite sent successfully!
    </fl-toast-alert>
  `,
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements AfterViewInit, OnInit, OnDestroy {
  AwardProjectHireMeState = AwardProjectHireMeState;
  BackgroundColor = BackgroundColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardBorderRadius = CardBorderRadius;
  CardSize = CardSize;
  ContainerSize = ContainerSize;
  Feature = Feature;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  LayoutType = LayoutType;
  Margin = Margin;
  ModalSize = ModalSize;
  RoleApi = RoleApi;
  UserProfileSection = UserProfileSection;
  TextTransform = TextTransform;
  UserProfileViewState = UserProfileViewState;
  SplitButtonClickEvent = SplitButtonClickEvent;
  FollowUserToastItem = FollowUserToastItem;
  RecommendUserToastItem = RecommendUserToastItem;
  ToastAlertType = ToastAlertType;

  @Input() user$: Rx.Observable<User>;

  @ViewChild('pageTop', { read: ElementRef })
  pageTop: ElementRef;
  @ViewChild('summary', { read: ElementRef })
  summary: ElementRef;
  @ViewChild('portfolio', { read: ElementRef })
  portfolio: ElementRef;
  @ViewChild('reviews', { read: ElementRef })
  reviews: ElementRef;
  @ViewChild('resume', { read: ElementRef })
  resume: ElementRef;
  summarySectionObserver?: IntersectionObserver;
  portfolioSectionObserver?: IntersectionObserver;
  reviewsSectionObserver?: IntersectionObserver;
  resumeSectionObserver?: IntersectionObserver;
  userProfileSection?: UserProfileSection;
  private portfolioObserverSubscription?: Rx.Subscription;
  private reviewsObserverSubscription?: Rx.Subscription;
  private resumeObserverSubscription?: Rx.Subscription;

  private _isFeaturedReviewEditMode$ = new Rx.BehaviorSubject(false);
  isFeaturedReviewEditMode$ = this._isFeaturedReviewEditMode$.asObservable();

  private _currentViewState$ = new Rx.BehaviorSubject<UserProfileViewState>(
    UserProfileViewState.VIEW,
  );
  currentViewState$ = this._currentViewState$.asObservable();

  private _viewProfileAs$ = new Rx.BehaviorSubject<RoleApi>(RoleApi.FREELANCER);
  viewProfileAs$ = this._viewProfileAs$.asObservable();

  private _croppedCoverImage$ = new Rx.Subject<CoverImageStyle>();

  private _triggerSaveSummaryChanges$ = new Rx.Subject<void>();
  triggerSaveSummaryChanges$ = this._triggerSaveSummaryChanges$.asObservable();

  private _triggerCancelSummaryChanges$ = new Rx.Subject<void>();
  triggerCancelSummaryChanges$ = this._triggerCancelSummaryChanges$.asObservable();

  private _savingSummaryChanges$ = new Rx.BehaviorSubject<boolean>(false);
  savingSummaryChanges$ = this._savingSummaryChanges$.asObservable();

  resumeExists$: Rx.Observable<boolean>;

  private triggerSkillsRetry$ = new Rx.BehaviorSubject<number>(1);
  triggerLimit = 1;

  authId$: Rx.Observable<number | undefined>;
  awardProjectBid$: Rx.Observable<AwardProjectModel>;
  awardProjectHireMeState$: Rx.Observable<AwardProjectHireMeState>;
  coverImage$: Rx.Observable<CoverImageStyle | undefined>;
  coverImageToShow$: Rx.Observable<CoverImageStyle | undefined>;
  hireMeUser$: Rx.Observable<HireMeUser>;
  isAgentLoggedInUser$: Rx.Observable<boolean>;
  isAgentUserProfile$: Rx.Observable<boolean>;
  isCorporate$: Rx.Observable<boolean>;
  isDeloitteUserProfile$: Rx.Observable<boolean>;
  isLoggedIn$: Rx.Observable<boolean>;
  isOnline$: Rx.Observable<boolean>;
  isViewerProfileOwner$: Rx.Observable<boolean>;
  localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  localHourlyRateLoading$: Rx.Observable<boolean>;
  profileDisplayName$: Rx.Observable<string>;
  profileUserId$: Rx.Observable<number>;
  profileUserRole$: Rx.Observable<RoleApi>;
  userReputation$: Rx.Observable<
    FreelancerReputationWithRehire | EmployerReputation
  >;
  validPortfolios$: Rx.Observable<readonly PortfolioItem[]>;
  maxFollowLimit$: Rx.Observable<number>;

  articlesCollection: DatastoreCollection<ArticlesCollection>;
  certificationsCollection: DatastoreCollection<CertificationsCollection>;
  educationsCollection: DatastoreCollection<EducationsCollection>;
  experiencesCollection: DatastoreCollection<ExperiencesCollection>;
  featuredReviewsCollection: DatastoreCollection<ReviewsCollection>;
  portfoliosCollection: DatastoreCollection<PortfoliosCollection>;
  profileViewUsersDocument: DatastoreDocument<ProfileViewUsersCollection>;
  publicationsCollection: DatastoreCollection<PublicationsCollection>;
  skillsCollection: DatastoreCollection<TopSkillsCollection>;

  hasCommunity$: Rx.Observable<boolean>;
  refProject$: Rx.Observable<ProjectViewProject | undefined>;
  showSecondaryNavigation$: Rx.Observable<boolean>;
  showSecondarySkillsNavigation$: Rx.Observable<boolean>;
  showStickyHeader$: Rx.Observable<boolean>;
  showAwardProjectBanner$: Rx.Observable<boolean>;
  showEditCoverPhoto$: Rx.Observable<boolean>;
  showNextPreviousUsers$: Rx.Observable<boolean>;
  showReportUser$: Rx.Observable<boolean>;
  showSwitchUsers$: Rx.Observable<boolean>;
  showUsersPortfolio$: Rx.Observable<boolean>;
  showUsersResume$: Rx.Observable<boolean>;
  showUsersReviews$: Rx.Observable<boolean>;

  profileViewUser$: Rx.Observable<ProfileViewUser>;

  private setTitleSubscription?: Rx.Subscription;
  private userChangedSubscription?: Rx.Subscription;

  readonly defaultCurrency: Currency = {
    code: 'USD',
    sign: '$',
    id: 1,
    exchangeRate: 1,
  };

  isShowcase: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
    private assets: Assets,
    private auth: Auth,
    private datastore: Datastore,
    private featureFlagService: FeatureFlagsService,
    private modalService: ModalService,
    private route: ActivatedRoute,
    private seo: Seo,
    private toastAlertService: ToastAlertService,
    private tracking: Tracking,
  ) {}

  ngOnInit(): void {
    // TODO: Remove once migration of portfolo-item are done T247767
    this.isShowcase = this.route.snapshot.queryParams.isShowcase === 't';

    this.isLoggedIn$ = this.auth.isLoggedIn();
    this.authId$ = this.auth.getUserId().pipe(
      startWith(undefined),
      map(id => toNumber(id)),
    );

    this.profileUserId$ = this.user$.pipe(map(u => u.id));
    this.profileUserRole$ = this.user$.pipe(map(u => u.role));
    this.profileDisplayName$ = this.user$.pipe(map(u => u.displayName));
    this.isAgentUserProfile$ = this.user$.pipe(map(u => !!u.supportStatus));

    this.isDeloitteUserProfile$ = this.user$.pipe(
      map(user => !!user.isDeloitteDcUser),
    );

    this.isViewerProfileOwner$ = Rx.combineLatest([
      this.profileUserId$,
      this.authId$,
    ]).pipe(map(([profileUserId, userId]) => profileUserId === userId));

    // This should be changed back to an object call instead of query when the
    // freelancer reputation endpoint accepts batched requests.
    const freelancerReputation$ = this.datastore
      .document<FreelancerReputationsRehireRatesCollection>(
        'freelancerReputationsRehireRates',
        this.profileUserId$,
      )
      .valueChanges();

    const employerReputation$ = this.datastore
      .document<EmployerReputationsRehireRatesCollection>(
        'employerReputationsRehireRates',
        this.profileUserId$,
      )
      .valueChanges();

    this.userReputation$ = this.viewProfileAs$.pipe(
      switchMap(viewProfileAs =>
        viewProfileAs === RoleApi.FREELANCER
          ? freelancerReputation$
          : employerReputation$,
      ),
    );

    this.certificationsCollection = this.datastore.collection<CertificationsCollection>(
      'certifications',
      query => query.where('userId', '==', this.profileUserId$),
    );

    this.publicationsCollection = this.datastore.collection<PublicationsCollection>(
      'publications',
      query => query.where('userId', '==', this.profileUserId$),
    );

    this.experiencesCollection = this.datastore.collection<ExperiencesCollection>(
      'experiences',
      query => query.where('userId', '==', this.profileUserId$),
    );

    this.educationsCollection = this.datastore.collection<EducationsCollection>(
      'educations',
      query => query.where('userId', '==', this.profileUserId$),
    );

    this.resumeExists$ = Rx.combineLatest([
      this.certificationsCollection.valueChanges(),
      this.publicationsCollection.valueChanges(),
      this.experiencesCollection.valueChanges(),
      this.educationsCollection.valueChanges(),
      this.isViewerProfileOwner$,
    ]).pipe(
      map(
        ([certifications, publications, experiences, educations, isOwner]) =>
          isOwner ||
          certifications.length > 0 ||
          publications.length > 0 ||
          experiences.length > 0 ||
          educations.length > 0,
      ),
    );

    const projectIdProfileHasBidOn$ = this.datastore
      .collection<BidsCollection>('bids', query =>
        this.isLoggedIn$.pipe(
          map(loggedIn =>
            loggedIn
              ? query
                  .where('bidderId', '==', this.profileUserId$)
                  .where('projectOwnerId', '==', this.authId$)
                  .where('frontendBidStatus', '==', FrontendBidStatusApi.ACTIVE)
                  .limit(1)
              : query.null(),
          ),
        ),
      )
      .valueChanges()
      .pipe(map(bids => (bids[0] ? bids[0].projectId : undefined)));

    const awardProjectId$ = Rx.combineLatest([
      this.route.queryParamMap.pipe(
        map(params => toNumber(params.get('ref_project_id'))),
      ),
      this.authId$,
      this.profileUserId$,
      projectIdProfileHasBidOn$.pipe(startWith(undefined)),
    ]).pipe(
      map(
        ([refProjectId, authId, profileUserId, projectIdProfileHasBidOn]) =>
          refProjectId ||
          (authId === profileUserId ? undefined : projectIdProfileHasBidOn),
      ),
      filter(isDefined),
    );

    const awardProjectDocument = this.datastore.document<ProjectViewProjectsCollection>(
      'projectViewProjects',
      awardProjectId$,
    );

    const projectRefOwnerId$ = awardProjectDocument
      .valueChanges()
      .pipe(map(project => project.ownerId));

    const authUserId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));
    const showAwardProjectBanner$ = Rx.combineLatest([
      awardProjectDocument.valueChanges().pipe(startWith(undefined)),
      authUserId$,
    ]).pipe(
      map(([project, loggedInUserId]) =>
        // Check if the current user is either the project owner or is a
        // collaborator with BID_AWARD permissions.
        project && project.status !== ProjectStatusApi.CLOSED
          ? project.ownerId === loggedInUserId ||
            !!project.projectCollaborations.find(
              collaborator =>
                collaborator.userId === loggedInUserId &&
                collaborator.permissions.includes(
                  ProjectCollaborationPermissionApi.BID_AWARD,
                ),
            )
          : false,
      ),
    );

    const awardProjectBidsCollection = this.datastore.collection<BidsCollection>(
      'bids',
      query =>
        showAwardProjectBanner$.pipe(
          map(showAwardProjectBanner =>
            showAwardProjectBanner
              ? query
                  .where(
                    'projectId',
                    '==',
                    awardProjectId$.pipe(map(id => toNumber(id))),
                  )
                  .where('frontendBidStatus', '==', FrontendBidStatusApi.ACTIVE)
                  .where('bidderId', '==', this.profileUserId$)
                  .where('projectOwnerId', '==', projectRefOwnerId$)
              : query.null(),
          ),
        ),
    );

    const awardProjectBid$ = awardProjectBidsCollection.valueChanges().pipe(
      map(bids =>
        bids.find(
          bid =>
            !bid.timeAwarded &&
            ((bid.frontendBidStatus === FrontendBidStatusApi.ACTIVE &&
              bid.awardStatus !== BidAwardStatusApi.AWARDED) ||
              bid.awardStatus !== BidAwardStatusApi.PENDING),
        ),
      ),
      filter(isDefined),
    );

    this.profileViewUsersDocument = this.datastore.document<ProfileViewUsersCollection>(
      'profileViewUsers',
      this.profileUserId$,
    );

    this.profileViewUser$ = this.profileViewUsersDocument.valueChanges();

    const profileViewUsersStatus$ = this.profileViewUsersDocument.status$;

    this.awardProjectBid$ = Rx.combineLatest([
      awardProjectBid$,
      this.profileViewUser$,
      awardProjectDocument.valueChanges(),
    ]).pipe(
      map(([bid, bidder, project]) => ({
        projectName: project.title,
        hourlyRate: bidder.hourlyRate,
        bidDesc: bid.description,
        bidId: bid.id,
        projectId: bid.projectId,
        freelancerDisplayName: bidder.publicName,
        freelancerId: bidder.id,
        projectLink: `/projects/${project.seoUrl}`,
        bidAmount: bid.amount,
        duration: bid.period,
        currency: project.currency,
        hourly: project.type === ProjectTypeApi.HOURLY,
        commitment: project.hourlyProjectInfo
          ? project.hourlyProjectInfo.commitment
          : undefined,
      })),
    );

    const awardProjectBidLoading$ = awardProjectBidsCollection.status$.pipe(
      map(status => !status.ready),
    );

    this.showEditCoverPhoto$ = Rx.combineLatest([
      this._currentViewState$,
      this.isViewerProfileOwner$,
      this.featureFlagService.getFlag(Feature.MEMBERSHIPS),
    ]).pipe(
      map(
        ([viewState, isOwner, membership]) =>
          isOwner && viewState === UserProfileViewState.EDIT && membership,
      ),
    );

    this.awardProjectHireMeState$ = Rx.combineLatest([
      this.isLoggedIn$,
      this.isViewerProfileOwner$,
      awardProjectId$.pipe(startWith(undefined)),
      awardProjectBidLoading$.pipe(startWith(true)),
      awardProjectBid$.pipe(startWith(undefined)),
      showAwardProjectBanner$.pipe(startWith(false)),
    ]).pipe(
      map(
        ([
          loggedIn,
          isViewerProfileOwner,
          awardProjectId,
          loading,
          awardProjectBid,
          showAwardProjectBanner,
        ]) => {
          // If the currently logged in user owns the viewed profile no actions
          // should be shown.
          if (isViewerProfileOwner) {
            return AwardProjectHireMeState.NONE;
          }

          // No project award project found, show HireMe.
          if (!awardProjectId || !loggedIn || !showAwardProjectBanner) {
            return AwardProjectHireMeState.HIRE_ME;
          }
          // Still checking if the given ref project id is valid. Show a loading
          // state.
          if (loading) {
            return AwardProjectHireMeState.LOADING;
          }

          // If award project bid is defined the award project component should
          // be shown instead of the hire me component.
          if (awardProjectBid && showAwardProjectBanner) {
            return AwardProjectHireMeState.AWARD_PROJECT;
          }

          // Default to the hire me component.
          return AwardProjectHireMeState.HIRE_ME;
        },
      ),
    );

    this.isOnline$ = this.datastore
      .document<OnlineOfflineCollection>('onlineOffline', this.profileUserId$)
      .valueChanges()
      .pipe(
        map(status => status.status === OnlineOfflineStatusApi.ONLINE),
        startWith(false),
      );

    this.showReportUser$ = Rx.combineLatest([
      this.isLoggedIn$,
      this.isViewerProfileOwner$,
    ]).pipe(
      map(
        ([isLoggedIn, isViewerProfileOwner]) =>
          isLoggedIn && !isViewerProfileOwner,
      ),
    );

    this.coverImage$ =
      this.uiConfig.theme === 'deloitte'
        ? Rx.of({
            backgroundImage: `url(${this.assets.getUrl(
              'profiles/cover-product.jpg',
            )})`,
            opacity: this.uiConfig.theme === 'deloitte' ? 1 : 0.8,
          })
        : this.profileViewUser$.pipe(
            map(user =>
              user.coverImage && user.coverImage.currentImage
                ? {
                    backgroundImage: `url('${user.coverImage.currentImage.url}')`,
                  }
                : undefined,
            ),
          );

    this.hasCommunity$ = this.user$.pipe(
      map(
        user => isDefined(user.poolIds) && user.poolIds.includes(Pool.FACEBOOK),
      ),
    );

    this.isCorporate$ = this.profileViewUser$.pipe(
      map(
        u => !!u.corporate && u.corporate.status === CorporateStatusApi.ACTIVE,
      ),
    );

    const usersDoc = this.datastore.document<UsersCollection>(
      'users',
      this.auth.getUserId(),
    );
    const loggedInUsersStatus$ = usersDoc.status$;

    this.isAgentLoggedInUser$ = usersDoc
      .valueChanges()
      .pipe(map(u => u.supportStatus !== undefined));

    const loggedUserHasProjects$ = this.datastore
      .collection<ProjectsCollection>('projects', query =>
        query.where('ownerId', '==', authUserId$).limit(1),
      )
      .valueChanges()
      .pipe(map(projects => projects.length > 0));

    this.hireMeUser$ = Rx.combineLatest([
      this.profileViewUser$,
      usersDoc.valueChanges(),
      loggedUserHasProjects$,
    ]).pipe(
      map(([profileUser, loggedInUser, loggedUserHasProjects]) => ({
        authId: loggedInUser.id,
        profileUserId: profileUser.id,
        enterpriseIds: profileUser.enterpriseIds,
        profileUserHourlyRate: profileUser.hourlyRate,
        isDeloitteDcUser: !!profileUser.isDeloitteDcUser,
        profileUserDisplayName: profileUser.publicName || profileUser.username,
        profileUserLocation: profileUser.location,
        paymentVerified: loggedInUser.status.paymentVerified,
        emailVerified: loggedInUser.status.emailVerified,
        defaultCurrency: loggedInUser.currency,
        loggedUserIsRookie: !loggedUserHasProjects,
      })),
    );

    const profileHourlyRate$ = this.profileViewUser$.pipe(
      map(user => user.hourlyRate),
    );

    // Used to show a loading state for the converted hourly rate.
    const loadingLoggedInStatus$ = Rx.combineLatest([
      loggedInUsersStatus$.pipe(startWith(undefined)),
      profileViewUsersStatus$.pipe(startWith(undefined)),
    ]).pipe(
      map(([loggedInUser, userProfile]) =>
        loggedInUser && userProfile
          ? !(loggedInUser.ready && userProfile.ready)
          : true,
      ),
    );

    this.localHourlyRateLoading$ = this.isLoggedIn$.pipe(
      switchMap(isLoggedIn =>
        isLoggedIn ? loadingLoggedInStatus$ : Rx.of(false),
      ),
    );

    this.localHourlyRate$ = profileHourlyRate$.pipe(
      map(profileHourlyRate => {
        if (!profileHourlyRate) {
          return undefined;
        }

        return {
          currency: this.defaultCurrency,
          rate: Math.ceil(profileHourlyRate),
        };
      }),
    );

    this.showSecondarySkillsNavigation$ = Rx.combineLatest([
      this.isLoggedIn$,
      this.profileUserId$,
    ]).pipe(
      map(([isLoggedIn, id]) => {
        // This duplicates the legacy logic from: ABTest.php::addSkillSubnav
        // It's not actually a part of any ABTest and is meant to be kept both for
        // legacy feature parity and SEO purposes.
        // Ref: T58858
        const hashedString = Md5.hashStr(`${id}wtjoT58858`)
          .toString()
          .substring(0, 8);
        return !isLoggedIn && parseInt(hashedString, 16) / 0xffffffff <= 0.5;
      }),
    );

    this.articlesCollection = this.datastore.collection<ArticlesCollection>(
      'articles',
      query =>
        query
          .where('userId', '==', this.profileUserId$)
          .where('state', '==', ArticleStateApi.APPROVED),
    );

    this.skillsCollection = this.datastore.collection<TopSkillsCollection>(
      'topSkills',
      query =>
        Rx.combineLatest([
          this.profileUserId$,
          this.triggerSkillsRetry$.asObservable(),
        ]).pipe(
          map(([profileUserId, triggerLimit]) =>
            // The limit is used to allow the request to be re triggered by
            // changing the limit.
            query.where('userId', '==', profileUserId).limit(triggerLimit),
          ),
        ),
    );

    this.featuredReviewsCollection = this.datastore.collection<ReviewsCollection>(
      'reviews',
      query =>
        query
          .where('featured', '==', true)
          .where('toUserId', '==', this.profileUserId$)
          .where('reviewType', 'in', [
            ReviewTypeApi.CONTEST,
            ReviewTypeApi.PROJECT,
          ]),
    );

    this.portfoliosCollection = this.datastore.collection<PortfoliosCollection>(
      'portfolios',
      query => query.where('userId', '==', this.profileUserId$),
    );

    this.setTitleSubscription = this.profileViewUser$.subscribe(user => {
      const displayName = user.publicName || user.username;
      const skills =
        user.skills && user.skills.length >= 2
          ? [user.skills[0].name, user.skills[1].name]
          : undefined;
      const country =
        user.location && user.location.country
          ? user.location.country.name
          : undefined;

      let description: string;

      if (country && skills) {
        description = `${displayName} is a Freelancer specialising in ${skills[0]} and ${skills[1]} in ${country}.`;
      } else if (country) {
        description = `${displayName} is a Freelancer in ${country}.`;
      } else if (skills) {
        description = `${displayName} is a Freelancer specialising in ${skills[0]} and ${skills[1]}.`;
      } else {
        description = `${displayName} is a Freelancer.`;
      }

      const isIndexed =
        user.avatarLarge &&
        user.profileDescription &&
        user.profileDescription.length > 150;

      this.seo.setPageTags({
        title: `${displayName} Profile`,
        description,
        noIndex: !isIndexed,
      });
    });

    this.showUsersPortfolio$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
      this.isAgentUserProfile$,
      this.portfoliosCollection.valueChanges(),
      this.viewProfileAs$,
      this.isDeloitteUserProfile$,
      this.featureFlagService.getFlag(Feature.PORTFOLIO),
    ]).pipe(
      map(
        ([
          isViewerProfileOwner,
          isAgentUserProfile,
          portfolios,
          viewProfileAs,
          isDeloitteUserProfile,
          portfolioFlag,
        ]) => {
          if (!portfolioFlag) {
            return false;
          }

          if (isDeloitteUserProfile) {
            return false;
          }

          if (viewProfileAs === RoleApi.EMPLOYER) {
            return false;
          }

          if (
            !isViewerProfileOwner &&
            (!portfolios || portfolios.length === 0)
          ) {
            return false;
          }

          if (isAgentUserProfile) {
            return false;
          }

          return true;
        },
      ),
    );

    this.showSecondaryNavigation$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
    ]).pipe(map(([isViewerProfileOwner]) => isViewerProfileOwner));

    this.showStickyHeader$ = Rx.combineLatest([this.isAgentUserProfile$]).pipe(
      map(([isAgentUserProfile]) => !isAgentUserProfile),
    );

    this.showAwardProjectBanner$ = Rx.combineLatest([
      this.awardProjectHireMeState$,
    ]).pipe(
      map(
        ([awardProjectHireMeState]) =>
          awardProjectHireMeState === AwardProjectHireMeState.AWARD_PROJECT,
      ),
    );

    this.showUsersReviews$ = Rx.combineLatest([
      this.isAgentUserProfile$,
      this.isDeloitteUserProfile$,
    ]).pipe(
      map(
        ([isAgentProfile, isDeloitteProfile]) =>
          !isAgentProfile && !isDeloitteProfile,
      ),
    );

    this.showUsersResume$ = Rx.combineLatest([
      this.isAgentUserProfile$,
      this.featureFlagService.getFlag(Feature.PROFILE_RESUME),
    ]).pipe(
      map(
        ([isAgentProfile, profileResume]) => !isAgentProfile && profileResume,
      ),
    );

    this.showNextPreviousUsers$ = Rx.combineLatest([this.isLoggedIn$]).pipe(
      map(([isLoggedIn]) => !isLoggedIn),
    );

    const canViewEmployerProfile$ = this.datastore
      .document<CanViewEmployerProfileCollection>(
        'canViewEmployerProfile',
        this.profileUserId$,
      )
      .valueChanges()
      .pipe(map(response => response.canViewEmployerProfile));

    this.showSwitchUsers$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
      canViewEmployerProfile$,
    ]).pipe(
      map(
        ([isViewerOwner, canViewEmployerProfile]) =>
          isViewerOwner || canViewEmployerProfile,
      ),
    );

    this.coverImageToShow$ = Rx.combineLatest([
      this._croppedCoverImage$.pipe(startWith(undefined)),
      this.coverImage$,
    ]).pipe(
      map(([croppedCoverImage, coverImage]) => croppedCoverImage || coverImage),
    );

    this.refProject$ = this.datastore
      .collection<ProjectViewProjectsCollection>('projectViewProjects', query =>
        this.route.queryParams.pipe(
          map(params =>
            params.refproject &&
            !Number.isNaN(params.refproject) &&
            params.rehire === 'true'
              ? query
                  .where('id', '==', toNumber(params.refproject))
                  .where('ownerId', '==', authUserId$)
              : query.null(),
          ),
        ),
      )
      .valueChanges()
      .pipe(map(projects => (projects ? projects[0] : undefined)));

    // Display Hire Me modal on load if requested via query params.
    Rx.combineLatest([this.refProject$, this.route.queryParams])
      .pipe(
        filter(
          ([refProject, params]) =>
            (params.action === 'hireMe' ||
              params.hireMe === 'true' ||
              !!refProject) &&
            !params.next,
        ),
        take(1),
      )
      .toPromise()
      .then(([refProject]) =>
        this.openModal({
          event: SplitButtonClickEvent.HIRE_ME,
          params: {
            refProject,
          },
        }),
      );

    this.maxFollowLimit$ = this.datastore
      .document<MembershipBenefitsCollection>(
        'membershipBenefits',
        'employer_following',
      )
      .valueChanges()
      .pipe(map(benefit => benefit.value));

    const referenceBid$ = this.datastore
      .collection<BidsCollection>('bids', query =>
        this.route.queryParams.pipe(
          map(params =>
            params.ref_bid_id && !Number.isNaN(params.ref_bid_id)
              ? query.where('id', '==', toNumber(params.ref_bid_id))
              : query.null(),
          ),
        ),
      )
      .valueChanges()
      .pipe(
        map(bids => (bids ? bids[0] : undefined)),
        filter(isDefined),
      );

    Rx.combineLatest([
      this.route.queryParams.pipe(map(params => params.ref_project_id)),
      referenceBid$,
      awardProjectDocument.valueChanges(),
      authUserId$,
      this.profileUserId$,
    ])
      .pipe(
        filter(
          ([refProjectId, refBid, refProject, authUserId, profileUserId]) =>
            refBid.bidderId === profileUserId &&
            toNumber(refProjectId) === refProject.id &&
            refProject.ownerId === authUserId &&
            refBid.projectId === refProject.id,
        ),
        take(1),
      )
      .toPromise()
      .then(([_, refBid]) => {
        this.tracking.trackCustomEvent('ClientViewBidderProfile', undefined, {
          bid_id: refBid.id,
          bidder_id: refBid.bidderId,
        });
      });

    this.userChangedSubscription = this.user$
      .pipe(distinctUntilChanged())
      .subscribe(() => this._currentViewState$.next(UserProfileViewState.VIEW));
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      // User profile sections sticky header active section observers
      this.summarySectionObserver = new IntersectionObserver(
        entries => {
          if (entries.some(e => e.isIntersecting)) {
            this.userProfileSection = UserProfileSection.SUMMARY;
          }
        },
        // -90px margin to compensate for the sticky header
        { rootMargin: '-90px 0px -80% 0px' },
      );
      this.summarySectionObserver.observe(this.summary.nativeElement);

      this.portfolioObserverSubscription = this.showUsersPortfolio$.subscribe(
        showPortfolio => {
          if (showPortfolio) {
            this.portfolioSectionObserver = new IntersectionObserver(
              entries => {
                if (entries.some(e => e.isIntersecting)) {
                  this.userProfileSection = UserProfileSection.PORTFOLIO;
                }
              },
              // -90px margin to compensate for the sticky header
              { rootMargin: '-90px 0px -80% 0px' },
            );
            this.portfolioSectionObserver.observe(this.portfolio.nativeElement);
          } else if (this.portfolioSectionObserver) {
            this.portfolioSectionObserver.disconnect();
          }
        },
      );

      this.reviewsObserverSubscription = this.showUsersReviews$.subscribe(
        showReviews => {
          if (showReviews) {
            this.reviewsSectionObserver = new IntersectionObserver(
              entries => {
                if (entries.some(e => e.isIntersecting)) {
                  this.userProfileSection = UserProfileSection.REVIEWS;
                }
              },
              // -90px margin to compensate for the sticky header
              { rootMargin: '-90px 0px -80% 0px' },
            );
            this.reviewsSectionObserver.observe(this.reviews.nativeElement);
          } else if (this.reviewsSectionObserver) {
            this.reviewsSectionObserver.disconnect();
          }
        },
      );

      this.resumeObserverSubscription = Rx.combineLatest([
        this.showUsersResume$,
        this.resumeExists$,
      ]).subscribe(([showResume, resumeExists]) => {
        if (showResume && resumeExists) {
          this.resumeSectionObserver = new IntersectionObserver(
            entries => {
              if (entries.some(e => e.isIntersecting)) {
                this.userProfileSection = UserProfileSection.RESUME;
              }
            },
            // -90px margin to compensate for the sticky header
            { rootMargin: '-90px 0px -80% 0px' },
          );
          this.resumeSectionObserver.observe(this.resume.nativeElement);
        } else if (this.resumeSectionObserver) {
          this.resumeSectionObserver.disconnect();
        }
      });
    }
  }

  private openHireMeModal(refProject?: ProjectViewProject) {
    Rx.combineLatest([this.profileUserId$, this.isDeloitteUserProfile$])
      .pipe(take(1))
      .toPromise()
      .then(([profileUserId, isDeloitteUser]) => {
        this.modalService.open('HireMeWebappModal', {
          inputs: {
            profileUserId,
            refProject,
          },
          color: ModalColor.DARK,

          size: isDeloitteUser ? ModalSize.MID : ModalSize.XSMALL,
          mobileFullscreen: true,
          edgeToEdge: true,
        });
      });
  }

  private openSignUpModal(openModalParams: OpenModalParams) {
    this.modalService
      .open('LoginSignupModal', {
        inputs: {
          form: LoginOrSignup.SIGNUP,
        },
        size: ModalSize.XSMALL,
        edgeToEdge: true,
        closeable: true,
        mobileFullscreen: true,
      })
      .afterClosed()
      .toPromise()
      .then(response => {
        if (response) {
          this.openModal(openModalParams);
        }
      });
  }

  private openInviteModal() {
    Rx.combineLatest([this.profileUserId$, this.profileDisplayName$])
      .pipe(take(1))
      .toPromise()
      .then(([profileUserId, profileDisplayName]) => {
        this.modalService.open('InviteModal', {
          size: ModalSize.SMALL,
          edgeToEdge: false,
          mobileFullscreen: true,
          inputs: {
            freelancerId: profileUserId,
            freelancerName: profileDisplayName,
          },
        });
      });
  }

  private openModal(openModalParams: OpenModalParams) {
    this.isLoggedIn$
      .pipe(take(1))
      .toPromise()
      .then(isLoggedIn => {
        if (!isLoggedIn) {
          this.openSignUpModal(openModalParams);
          return;
        }

        switch (openModalParams.event) {
          case SplitButtonClickEvent.HIRE_ME:
            return this.openHireMeModal(openModalParams.params.refProject);

          case SplitButtonClickEvent.INVITE:
            return this.openInviteModal();

          default:
            return assertNever(
              openModalParams.event,
              'Invalid open modal param',
            );
        }
      });
  }

  showToastAlert(item: FollowUserToastItem | RecommendUserToastItem): void {
    this.toastAlertService.open(item);
  }

  handleSplitButtonClickEvent(event: SplitButtonClickEvent) {
    this.openModal({
      event,
      params: {},
    });
  }

  handleCancelEditProfile() {
    this._triggerCancelSummaryChanges$.next();
  }

  handleSaveEditProfile() {
    this._triggerSaveSummaryChanges$.next();
  }

  handleSavingSummary(state: boolean): void {
    this._savingSummaryChanges$.next(state);
  }

  handleProfileViewTypeChange(type: RoleApi): void {
    this._viewProfileAs$.next(type);
  }

  handleTriggerSkillsUpdate(): void {
    // To ensure the network request is called again we need to change the limit,
    // to allow the request to hide from the deduplication logic.
    this.triggerLimit++;
    this.triggerSkillsRetry$.next(this.triggerLimit);
  }

  handleImageCropped(base64: string): void {
    this._croppedCoverImage$.next({ backgroundImage: `url(${base64})` });
  }

  handleImageReset(): void {
    this._croppedCoverImage$.next(undefined);
  }

  handleToggleViewMode(state: UserProfileViewState): void {
    this._currentViewState$.next(state);
  }

  handleToggleFeaturedReviewEditModeChange(isEditMode: boolean) {
    this._isFeaturedReviewEditMode$.next(isEditMode);
  }

  ngOnDestroy(): void {
    if (this.setTitleSubscription) {
      this.setTitleSubscription.unsubscribe();
    }

    if (this.summarySectionObserver) {
      this.summarySectionObserver.disconnect();
    }

    if (this.portfolioObserverSubscription) {
      this.portfolioObserverSubscription.unsubscribe();

      if (this.portfolioSectionObserver) {
        this.portfolioSectionObserver.disconnect();
      }
    }

    if (this.reviewsObserverSubscription) {
      this.reviewsObserverSubscription.unsubscribe();

      if (this.reviewsSectionObserver) {
        this.reviewsSectionObserver.disconnect();
      }
    }

    if (this.resumeObserverSubscription) {
      this.resumeObserverSubscription.unsubscribe();

      if (this.resumeSectionObserver) {
        this.resumeSectionObserver.disconnect();
      }
    }

    if (this.userChangedSubscription) {
      this.userChangedSubscription.unsubscribe();
    }
  }
}
